import {
  ApolloClient,
  QueryHookOptions,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  CardBlock,
  CardCollectionAgendaTemplate,
  CardCollectionKanbanTemplateV2,
  CardCollectionListTemplateV1,
  CardCollectionListTemplateV2,
  Color,
  FieldExpression,
  GroupBy,
  GroupByCard,
  TaskListOptionsV1,
  evaluateSelector,
} from '@coast/schemas';
import { isNil, sortBy } from 'lodash';
import {
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Mutations, Queries } from '@shared/api';
import {
  CardPaginationCardFilterOption,
  CardPaginationFilter,
  CardPaginationQuery,
  useCardPaginationActions,
  useCardPaginationState,
} from '@shared/api/providers/cardPagination';
import { View } from '@shared/components/View';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { useAllTagOptions } from '@shared/components/blocks/lib/tags/hooks';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { correctMissingBlockData } from '@shared/components/blocks/utils/correctMissingBlockData';
import { defaultEvaluationContext } from '@shared/components/blocks/utils/tokens';
import { MBIconV2, MBUserImage } from '@shared/components/image';
import { MBTextBody1, MBTextBody1Bold, MBTextBody2 } from '@shared/components/typography';
import { CardFilterStateContext } from '@shared/scenes/cards/actions/provider';
import { CardSectionListData, selectCard, sortCards } from '@shared/scenes/cards/util';
import {
  useGetChannelEventHandlers,
  useWorkflowTemplateWithCardDefinition,
} from '@shared/scenes/workflows/hooks';
import { CARD_PAGINATION_SIZE, ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useChannel, useChannelUsers } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { usePermission } from '@shared/util/permissions/hooks';
import { MBSentry } from '@shared/util/sentry';
import { CardCollectionTemplate, CardSingleTemplate, CardTemplate } from './types';

export function useCardsForChannel(channelId: number): API.CardFragment[] {
  const { getState } = useCardPaginationState();
  const { fetchMore } = useCardPaginationActions();

  const query = useMemo<CardPaginationQuery>(() => {
    return { channelId, statuses: ['ACTIVE', 'PENDING'], order: [] };
  }, [channelId]);
  const section = useMemo(() => getState(query), [getState, query]);

  useEffect(() => {
    if (!section || (!section.loading && section.hasMore)) {
      fetchMore(query);
    }
  }, [query, section, fetchMore]);

  return useMemo(() => section?.cards ?? [], [section?.cards]);
}

export function useCard(
  id?: string,
  options: Partial<QueryHookOptions<API.CardDetailsQuery, API.CardDetailsQueryVariables>> = {},
): {
  card?: API.CardDetailsQuery['card'];
  loading: boolean;
  error?: Error;
} {
  const { data, loading, error } = useQuery<API.CardDetailsQuery, API.CardDetailsQueryVariables>(
    Queries.card.details,
    {
      ...options,
      variables: { id: id! },
      skip: isNil(id),
    },
  );
  return { loading, card: data?.card, error };
}
type GetCardFunction = (id?: string) => Promise<API.CardFragment | undefined>;

export function useGetCardLazyQuery(): GetCardFunction {
  const [getCard] = useLazyQuery<API.CardDetailsQuery, API.CardDetailsQueryVariables>(
    Queries.card.details,
  );
  return useCallback(
    async (id?: string) => {
      if (isNil(id)) {
        return undefined;
      }
      const result = await getCard({ variables: { id } });
      return result.data?.card ?? undefined;
    },
    [getCard],
  );
}

export function useRemoveCard(): (
  cardId: string,
  scope: API.EntityRecurrenceScope,
) => Promise<any> {
  const pagination = useCardPaginationActions();
  const [removeCard] = useMutation<API.RemoveCardMutation, API.RemoveCardMutationVariables>(
    Mutations.cards.remove,
  );

  return useCallback(
    async (cardId: string, scope: API.EntityRecurrenceScope) => {
      await removeCard({ variables: { input: { scope, id: cardId } } });
      pagination.removeCard(cardId);
    },
    [removeCard, pagination],
  );
}

export function useCardListSectionedData(
  cards: API.CardFragment[],
  template: CardCollectionListTemplateV1,
): CardSectionListData[] {
  const channel = useContext(CardChannelContext);
  const { cardDefinition } = useCardDefinition(channel?.id, channel?.workflowTemplateId);
  const { workflowTemplate } = useWorkflowTemplateWithCardDefinition(channel?.workflowTemplateId);
  const [collapsed, setCollapsed] = useState<boolean[]>(
    template.sections.map(section => section.initiallyCollapsed ?? false),
  );

  const createCardPermission = usePermission({
    action: 'create',
    subject: 'Card',
    channelId: channel?.id,
  });
  return useMemo(() => {
    return template.sections.map((section, idx): CardSectionListData => {
      const filteredCards = cards.filter(
        selectCard(section.selector ?? [], cardDefinition?.definition ?? {}),
      );
      const sortedCards = sortCards(
        filteredCards,
        section.sortBy,
        cardDefinition?.definition ?? {},
      );
      return {
        data: (() => {
          if (collapsed[idx]) {
            return [];
          }
          if (!!section.quickCreate) {
            return [{} as API.CardFragment, ...sortedCards];
          }
          return sortedCards;
        })(),
        cardCount: sortedCards.length,
        cardBlocks: correctMissingBlockData(section.cardBlocks, workflowTemplate?.components),
        cardTheme: section.cardTheme,
        quickCreate: createCardPermission.enabled ? section.quickCreate : undefined,
        emptyState: section.emptyState,
        taskOptions: template.taskOptions ?? ({} as TaskListOptionsV1),
        title: <MBTextBody1Bold>{section?.title}</MBTextBody1Bold>,
        collapsed: collapsed[idx],
        loading: false,
        hasMore: false,
        toggleCollapsed: section.collapsible
          ? () => {
              const v = [...collapsed];
              v[idx] = !v[idx];
              setCollapsed(v);
            }
          : undefined,
      };
    });
  }, [cards, template, cardDefinition, collapsed, createCardPermission, workflowTemplate]);
}

export function useCardDefinitions(
  channelId: number | undefined,
  workflowTemplateId?: string | null,
): {
  loading: boolean;
  cardDefinitions?: API.CardDefinitionFragment[];
} {
  const workflowTemplateData = useWorkflowTemplateWithCardDefinition(workflowTemplateId);
  const virtualCardDefinition = workflowTemplateData.workflowTemplate?.cardDefinition;
  const virtualCardDefinitions: API.CardDefinitionFragment[] = isNil(virtualCardDefinition)
    ? []
    : [virtualCardDefinition];
  const cardDefinitionsData = useQuery<
    API.ChannelCardDefinitionsQuery,
    API.ChannelCardDefinitionsQueryVariables
  >(Queries.channel.cardDefinitions, {
    variables: { input: { channelId } },
    skip: isNil(channelId),
  });
  const channelCardDefinitions = cardDefinitionsData.data?.listCardDefinitions;
  const cardDefinitions = isNil(workflowTemplateId)
    ? channelCardDefinitions ?? undefined
    : virtualCardDefinitions;
  const loading = workflowTemplateData.loading || cardDefinitionsData.loading;
  return useMemo(() => ({ cardDefinitions, loading }), [cardDefinitions, loading]);
}

export function useCardDefinition(
  channelId: number | undefined,
  workflowTemplateId: string | null | undefined,
  cardDefinitionId?: string | null,
): {
  loading: boolean;
  cardDefinition: API.CardDefinitionFragment | undefined;
} {
  const workflowTemplateData = useWorkflowTemplateWithCardDefinition(workflowTemplateId);
  const virtualCardDefinition = workflowTemplateData.workflowTemplate?.cardDefinition;
  const cardDefinitionsData = useCardDefinitions(channelId);
  const cardDefinitions = cardDefinitionsData.cardDefinitions;
  const channelCardDefinition = getChannelCardDefinition(cardDefinitions, cardDefinitionId);
  const cardDefinition = isNil(workflowTemplateId) ? channelCardDefinition : virtualCardDefinition;
  const loading = workflowTemplateData.loading || cardDefinitionsData.loading;
  return useMemo(() => ({ cardDefinition, loading }), [cardDefinition, loading]);
}

function useGetCardDefinition(
  channel?: API.ChannelFragment,
): (cardDefinitionId?: string) => API.CardDefinitionFragment | undefined {
  const workflowTemplateData = useWorkflowTemplateWithCardDefinition(channel?.workflowTemplateId);
  const virtualCardDefinition = workflowTemplateData.workflowTemplate?.cardDefinition;
  const { cardDefinitions } = useCardDefinitions(channel?.id);
  return useCallback(
    cardDefinitionId => {
      const channelCardDefinition = getChannelCardDefinition(cardDefinitions, cardDefinitionId);
      return isNil(channel?.workflowTemplateId) ? channelCardDefinition : virtualCardDefinition;
    },
    [cardDefinitions, channel?.workflowTemplateId, virtualCardDefinition],
  );
}

function getChannelCardDefinition(
  cardDefinitions: API.CardDefinitionFragment[] | undefined,
  cardDefinitionId: string | null | undefined,
): API.CardDefinitionFragment | undefined {
  if (isNil(cardDefinitionId)) {
    return cardDefinitions?.find(def => def.root);
  } else {
    return cardDefinitions?.find(def => def.id === cardDefinitionId);
  }
}

export function useResolveCardDefinition(): (
  cardDefinitionId?: string,
) => API.CardDefinitionFragment | undefined {
  const channel = useContext(CardChannelContext);
  return useGetCardDefinition(channel);
}

export function useCardTemplates(
  cardDefinition: API.CardDefinitionFragment | undefined,
): CardTemplate[] | undefined {
  return useMemo(() => {
    if (!cardDefinition) {
      return undefined;
    }
    const cardTemplates = cardDefinition?.templates ?? [];
    const templates = cardTemplates.map(t => t.template as CardTemplate);
    return sortBy(templates, t => ('default' in t ? -Number(t.default) : 0));
  }, [cardDefinition]);
}

export interface CardTemplateQuery {
  channelSlug?: string;
  cardDefinition?: API.CardDefinitionFragment;
  templateSlug?: string;
  context?: CardTemplate['context'];
  contexts?: CardTemplate['context'][];
  card?: API.CardFragment;
}

type TemplateFilter = (t: CardTemplate) => boolean;

export function filterBySlug(slug: string): TemplateFilter {
  return t => (t as CardSingleTemplate | CardCollectionTemplate).slug === slug;
}

/**
 * Finds the best-matching template from an array of templates based on filters and optional context order.
 * Optimized for performance in React Native environments with limited memory.
 *
 * @param templates - Array of available templates to search through.
 * @param filters - Array of filter functions to apply to templates.
 * @param contextOrderOverride - Optional array defining priority order of template contexts.
 *
 * @returns The best-matching template, or null if no match is found
 *
 * @remarks
 * - Returns the first template that passes all filters if no contextOrderOverride is provided.
 * - When contextOrderOverride is provided, returns the matching template with the highest priority context.
 * - Priority is determined by index in contextOrderOverride (lower index = higher priority).
 * - Returns null if no templates match or if input arrays are empty.
 */
export function resolveTemplate<T extends CardTemplate>(
  templates: CardTemplate[],
  filters: Array<TemplateFilter>,
  contextOrderOverride?: CardTemplate['context'][],
): T | null {
  // The implementation of this function has significant performance implications on React Native due to the potential
  // size of the objects in the templates array, the JavaScript engine, limited memory availability, and
  // garbage-collection thrashing. We intentionally use every optimization available.

  if (templates.length === 0 || filters.length === 0) {
    return null;
  }

  let bestMatch: T | null = null;
  let bestMatchIndex = Infinity;

  const contextMap: Map<CardTemplate['context'], number> | null = contextOrderOverride
    ? new Map(contextOrderOverride.map((context, index) => [context, index]))
    : null;

  for (let i = 0; i < templates.length; i++) {
    const template = templates[i] as T;
    const passes = filters.every(filter => filter(template));

    if (passes) {
      // If there is no context order override and the template passes all filters then it is a match. In this scenario,
      // the order of the templates is the priority.
      if (isNil(contextMap)) {
        return template;
      }

      const contextIndex = contextMap.get(template.context);

      // Only process templates that have a context in the context order override
      if (!isNil(contextIndex)) {
        // If the template's context is equal to the highest priority context then it is a match and we can avoid
        // processing the rest of the templates.
        if (contextIndex === 0) {
          return template;
        }

        // Otherwise, we will continue iterating through the templates and try to find the best match based on priority.
        if (contextIndex < bestMatchIndex) {
          bestMatch = template;
          bestMatchIndex = contextIndex;
        }
      }
    }
  }

  return bestMatch;
}

export function useCardTemplate<T extends CardTemplate>(
  query: CardTemplateQuery | undefined,
  obeyContextOrder: boolean = false,
): {
  template: T | null;
  queriedSlug: string | undefined;
  loading: boolean;
} {
  const channel = useChannel({ channelSlug: query?.channelSlug });
  const workflowTemplateId = channel?.workflowTemplateId;
  const { workflowTemplate } = useWorkflowTemplateWithCardDefinition(workflowTemplateId);
  const behaviorViewTemplate = !isNil(query?.card)
    ? workflowTemplate?.behavior?.onOpenCard?.selectors.find(selector =>
        evaluateSelector(selector.selector, query?.card?.fields ?? {}),
      )?.viewTemplateId
    : undefined;

  const templates = useCardTemplates(query?.cardDefinition);
  if (!templates || isNil(query?.cardDefinition)) {
    return { loading: true, template: null, queriedSlug: query?.templateSlug };
  }
  const filters: Array<(t: CardTemplate) => boolean> = [];
  const contextsInOrder: CardTemplate['context'][] = [];

  if (query?.templateSlug) {
    filters.push(filterBySlug(query?.templateSlug));
  } else if (!isNil(behaviorViewTemplate)) {
    filters.push(filterBySlug(behaviorViewTemplate));
  }
  if (query?.context) {
    filters.push(t => t.context === query?.context);
    contextsInOrder.push(query?.context);
  }
  if (query?.contexts) {
    filters.push(t => query?.contexts!.includes(t.context));
    const contexts = query?.contexts ?? [];
    contextsInOrder.push(...contexts);
  }
  if (query?.contexts || query?.context) {
    filters.push(t => {
      return (
        !query?.card ||
        !('selector' in t) ||
        !t.selector ||
        evaluateSelector(t.selector, query?.card.fields ?? {}, defaultEvaluationContext)
      );
    });
  }
  const template = resolveTemplate<T>(
    templates,
    filters,
    obeyContextOrder ? contextsInOrder : undefined,
  );
  if (!template) {
    MBSentry.breadcrumb({
      category: 'cards',
      message: "couldn't find template with specified query",
      data: {
        query,
      },
    });
  }
  return { loading: false, template, queriedSlug: query.templateSlug };
}

export function useStickyBlocks(blocks: CardBlock[]): {
  top: CardBlock[];
  scroll: CardBlock[];
  bottom: CardBlock[];
} {
  return useMemo(() => {
    let top: CardBlock[] = [];
    let scroll: CardBlock[] = [];
    let bottom: CardBlock[] = [];
    blocks?.forEach(block => {
      if (block.type !== 'no-code' && block.sticky === 'top') {
        top.push(block);
      } else if (block.type !== 'no-code' && block.sticky === 'bottom') {
        bottom.push(block);
      } else {
        scroll.push(block);
      }
    });
    return { top, scroll, bottom };
  }, [blocks]);
}

function updateCardInCache(apolloClient: ApolloClient<unknown>, card: API.CardFragment) {
  // Note(fant): we insert the card into the Apollo cache to
  // make projected cards render properly. Fetching them
  // from the API will result in 404. We don't do this for
  // regular cards since we only get card.thread in details
  // queries, not in list queries.
  apolloClient.writeQuery<API.CardDetailsQuery, API.CardDetailsQueryVariables>({
    data: {
      card: { ...card, reminders: null },
    },
    query: Queries.card.details,
    variables: { id: card.id },
  });
}

export function useOpenCardFromAllCardsView(): (
  card: API.CardFragment,
  channelSlug: string,
) => void {
  const apolloClient = useApolloClient();
  const getChannelEventHandlers = useGetChannelEventHandlers();
  return useCallback(
    async (card: API.CardFragment) => {
      updateCardInCache(apolloClient, card);
      const { onOpenCard } = await getChannelEventHandlers(card.id);
      onOpenCard(card);
    },
    [apolloClient, getChannelEventHandlers],
  );
}

export function useCardPaginationFilters(
  template:
    | CardCollectionListTemplateV2
    | CardCollectionKanbanTemplateV2
    | CardCollectionAgendaTemplate,
  users: API.ShortUserFragment[],
): CardPaginationFilter[] | undefined {
  const { appliedFilters } = useContext(CardFilterStateContext);
  const assigneeFilter = useCardPaginationAssigneeFilter(template, users);
  return useMemo(() => {
    const filters = [assigneeFilter, ...appliedFilters].filter(notNil);
    return filters.length === 0 ? undefined : filters;
  }, [appliedFilters, assigneeFilter]);
}

export function getCardPaginationAssigneeFilter(
  template:
    | CardCollectionListTemplateV2
    | CardCollectionKanbanTemplateV2
    | CardCollectionAgendaTemplate,
  findUser: (userId: number) => API.ShortUserFragment | undefined,
): CardPaginationFilter | undefined {
  if (template.selector?.length === 1) {
    const selector = template.selector[0] as FieldExpression;
    if (
      selector.field.endsWith('assignees') &&
      selector.op === 'includes' &&
      selector.value === '{me}'
    ) {
      const currentUserId = defaultEvaluationContext.resolveCurrentUserId?.();
      if (!currentUserId) {
        return undefined;
      }
      const user = findUser(currentUserId);
      if (!user) {
        return undefined;
      }
      return { type: 'user', mustHaveAnyIds: [currentUserId] };
    }
  }
  return undefined;
}

export function useCardPaginationAssigneeFilter(
  template:
    | CardCollectionListTemplateV2
    | CardCollectionKanbanTemplateV2
    | CardCollectionAgendaTemplate,
  users: API.ShortUserFragment[],
): CardPaginationFilter | undefined {
  return useMemo(() => {
    return getCardPaginationAssigneeFilter(template, userId => users?.find(u => u.id === userId));
  }, [template, users]);
}

export interface CardPaginationSection {
  query: CardPaginationQuery;
  label: ReactNode;
  disabled: boolean;
}

function useGroupByCardOptions(channel: API.ChannelFragment | undefined, groupBy?: GroupBy) {
  const channelId = channel?.id;
  const skipAll = !groupBy || groupBy.type !== 'card' || !groupBy.cardName || !channelId;

  const { data: relatedChannelsData } = useQuery<
    API.RelatedChannelAssociationsQuery,
    API.RelatedChannelAssociationsQueryVariables
  >(Queries.channel.getRelatedChannelAssociations, {
    variables: { id: channelId! },
    skip: skipAll,
  });

  const fromChannelId = useMemo(() => {
    if (skipAll) {
      return undefined;
    }
    const field = relatedChannelsData?.getChannel?.relatedChannelAssociations?.find(rel => {
      return rel.field === (groupBy as GroupByCard).cardName;
    });
    if (field) {
      return field.relatedChannelId;
    }
  }, [groupBy, relatedChannelsData, skipAll]);

  const { data: listCards } = useQuery<API.ListCardsQuery, API.ListCardsQueryVariables>(
    Queries.card.listCards,
    {
      variables: { input: { channelId: fromChannelId, limit: CARD_PAGINATION_SIZE } },
      skip: !fromChannelId,
    },
  );

  return listCards?.list?.cards;
}

export function useCardPaginationSections(
  template: CardCollectionKanbanTemplateV2 | CardCollectionListTemplateV2,
): CardPaginationSection[] {
  const channel = useContext(CardChannelContext);
  const { users } = useChannelUsers({ channelId: channel?.id });
  const templateSelectorFilter = useCardPaginationAssigneeFilter(template, users);

  const { appliedFilters } = useContext(CardFilterStateContext);

  const createSectionQuery = useCallback(
    (sectionFilter?: CardPaginationFilter): CardPaginationQuery => {
      return {
        filters: [
          ...appliedFilters,
          ...(!!templateSelectorFilter ? [templateSelectorFilter] : []),
          ...(!!sectionFilter ? [sectionFilter] : []),
        ],
        channelId: channel?.id,
        statuses: template.statuses ?? ['ACTIVE'],
        order:
          template.context === 'kanban'
            ? [{ orderBy: 'kanbanSortKey', orderDirection: 'DESC' }]
            : template.sortBy?.map(sort => {
                return {
                  // TODO(fant): update this in the UI template schema?
                  orderBy: sort.field as API.ListCardsOrderBy,
                  orderDirection: sort.direction,
                };
              }),
      };
    },
    [appliedFilters, templateSelectorFilter, channel?.id, template],
  );

  const groups = useCardGroups(template.groupBy);
  return useMemo(() => {
    return (groups ?? []).map(group => {
      return {
        query: createSectionQuery(group.filter),
        label: group.label,
        disabled: group.disabled,
      };
    });
  }, [groups, createSectionQuery]);
}

interface CardGroup {
  filter?: CardPaginationFilter;
  label: ReactElement;
  disabled: boolean;
}

export function useCardGroups(groupBy?: GroupBy): CardGroup[] | undefined {
  const tags = useAllTagOptions().options;
  const channel = useContext(CardChannelContext);
  const { users } = useChannelUsers({ channelId: channel?.id });
  const cardOptions = useGroupByCardOptions(channel, groupBy);

  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  return useMemo(() => {
    const TypoComponent = renderContext === 'calendar' ? MBTextBody2 : MBTextBody1;
    const iconSize = renderContext === 'calendar' ? 16 : 24;

    if (!groupBy) {
      return [
        {
          label: <TypoComponent>{i18n('card.list.allCards')}</TypoComponent>,
          filter: undefined,
          disabled: false,
        },
      ];
    }

    switch (groupBy.type) {
      case 'card': {
        if (!cardOptions) {
          return undefined;
        }
        const sortedOptions = sortBy(cardOptions, 'name');
        const cardSections: CardGroup[] = sortedOptions.map(card => {
          return {
            label: (
              <View row centerVertically>
                <MBIconV2 name="arrow-link-out" color={colors.foregroundActive} size={iconSize} />
                <TypoComponent numberOfLines={1} style={{ marginLeft: grid(1) }}>
                  {card.fields?.name}
                </TypoComponent>
              </View>
            ),
            filter: {
              type: 'card',
              mustHaveAll: [{ cardId: card.id, fieldName: groupBy.cardName }],
            },
            disabled: false,
          };
        });

        if (!groupBy.noCardSection?.enabled) {
          return cardSections;
        }

        const noCardSection: CardGroup = {
          filter: {
            type: 'card',
            mustNotHaveAny: cardOptions.map<CardPaginationCardFilterOption>(card => ({
              cardId: card.id,
              fieldName: groupBy.cardName,
            })),
          },
          label: (
            <TypoComponent>
              {groupBy.noCardSection.title ?? i18n('card.list.noCardSectionDefaultTitle')}
            </TypoComponent>
          ),
          disabled: false,
        };

        return [noCardSection, ...cardSections];
      }

      case 'tag': {
        if (!tags) {
          return undefined;
        }
        const tagsWithName = tags.filter(tag => tag.fieldName === groupBy.tagName);
        const tagSections = tagsWithName
          .sort((a, b) => {
            if (a.archived === b.archived) {
              return 0;
            }
            return a.archived ? 1 : -1;
          })
          .map<CardGroup>(tag => {
            return {
              filter: { type: 'tag', mustHaveAll: [tag] },
              label: (
                <Tag
                  color={tag.backgroundColor as Color}
                  archived={tag.archived}
                  label={tag.label}
                />
              ),
              disabled: tag.archived,
            };
          });

        if (!groupBy.noTagSection?.enabled) {
          return tagSections;
        }

        const noTagSection: CardGroup = {
          filter: { type: 'tag', mustNotHaveAny: tagsWithName },
          label: (
            <TypoComponent>
              {groupBy.noTagSection.title ?? i18n('card.list.noTagSectionDefaultTitle')}
            </TypoComponent>
          ),
          disabled: false,
        };

        return [noTagSection, ...tagSections];
      }

      case 'assignees': {
        if (!channel) {
          return undefined;
        }
        const sortedUsers = sortBy(users, user => user.fullName.toLowerCase());
        const userSections = sortedUsers.map<CardGroup>(user => {
          return {
            filter: { type: 'user', mustHaveAllIds: [user.id] },
            label: (
              <View row centerVertically>
                <MBUserImage
                  size="small"
                  user={user}
                  style={{
                    width: iconSize,
                    height: iconSize,
                    borderRadius: iconSize / 2,
                    marginRight: grid(1),
                  }}
                />
                <TypoComponent numberOfLines={1} style={{ flex: 1 }}>
                  {user.fullName}
                </TypoComponent>
              </View>
            ),
            disabled: false,
          };
        });

        const noAssigneeSection: CardGroup = {
          filter: { type: 'user', mustNotHaveAnyIds: users.map(user => user.id) },
          label: <TypoComponent>{i18n('card.list.noAssigneeSectionDefaultTitle')}</TypoComponent>,
          disabled: false,
        };

        return [noAssigneeSection, ...userSections];
      }
    }
  }, [renderContext, groupBy, cardOptions, tags, channel, users, colors.foregroundActive]);
}
